import CheckIcon from "@mui/icons-material/Check";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import { type KeyboardEvent, useState } from "react";

import { zinc } from "@/ui/tokens";

/** The look of one picker entry: highlighted by the keys or the pointer, brighter when it is the chosen one. */
export const optionSx = (selected: boolean) =>
  ({
    width: "100%",
    justifyContent: "flex-start",
    gap: 0.75,
    px: 1,
    py: 0.375,
    borderRadius: 1,
    fontSize: "0.8125rem",
    textAlign: "left",
    color: selected ? "white" : zinc[200],
    "&:hover, &[data-highlighted=true]": { bgcolor: zinc[700], color: "white" },
  }) as const;

/**
 * The keyboard model the page's pickers share: the arrows walk a list of `count` entries, stopping at either end.
 *
 * `walk` handles an arrow key and says whether it did, so the picker can decide what else a key does; `index` is the
 * highlighted entry, clamped to the list as it now stands, which is what Enter commits.
 *
 * @param count - How many entries the list holds now.
 */
export function useListHighlight(count: number): {
  index: number;
  setHighlighted: (index: number) => void;
  walk: (event: KeyboardEvent) => boolean;
} {
  const [highlighted, setHighlighted] = useState(0);
  const walk = (event: KeyboardEvent): boolean => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return false;
    event.preventDefault();
    const step = event.key === "ArrowDown" ? 1 : -1;
    setHighlighted((index) => Math.min(count - 1, Math.max(0, index + step)));
    return true;
  };
  return { index: Math.min(highlighted, count - 1), setHighlighted, walk };
}

/**
 * One picker entry: its label, cut short when it runs long, and a check when it is the chosen one.
 *
 * The pointer moves the highlight as the arrows do, and a click picks the entry as Enter does.
 */
export function PickerOption({
  label,
  highlighted,
  selected,
  checked = selected,
  checkLabel,
  testId,
  autoFocus,
  onHighlight,
  onPick,
}: {
  label: string;
  highlighted: boolean;
  /** Whether the entry is the one in force, which the list announces as selected. */
  selected: boolean;
  /** Whether the entry shows the check; the selected one unless the picker marks another. */
  checked?: boolean;
  /** What the check means, for a screen reader. */
  checkLabel?: string;
  testId?: string;
  autoFocus?: boolean;
  onHighlight: () => void;
  onPick: () => void;
}) {
  return (
    <ButtonBase
      role="option"
      aria-selected={selected}
      autoFocus={autoFocus}
      data-sb={testId}
      data-highlighted={highlighted}
      onMouseEnter={onHighlight}
      onClick={onPick}
      sx={optionSx(selected)}
    >
      <Box component="span" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </Box>
      {checked && <CheckIcon aria-label={checkLabel} sx={{ fontSize: 14, color: "var(--color-brand-primary-400)" }} />}
    </ButtonBase>
  );
}
